import React, { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { NavigationModuleProvider } from "@agp/shared.user-app/application/context/navigation";
import { PagePaths } from "@agp/shared.user-app/application/context/navigation/type";
import { NavigationCoreModuleProvider } from "../../../domain/context/navigation-core";
import { useNavigationCoreModule } from "../../../domain/module/navigation-core";
import { useQueryModule } from "../../../domain/module/query";
import { useNavigationModule } from "../../../domain/module/navigation";
import { applicationConfig } from "../../../infrastructure/application-config";
import ServiceLocator from "../../../domain/service-locator";
import { ApplicationDesignSettings } from "../../../infrastructure/application-design-service";
import { useLanguageBasedFont } from "./const/font-utils";

// Monotone版のページインポート (一旦generalテンプレートを使用)
import { MonotoneTopPage } from "./pages/top/monotone-top";
import { MonotoneNotePage } from "./pages/note/monotone-note-page";
import { MonotoneGuidesPage } from "./pages/guides/monotone-guides-page";
import { MonotoneRootPage } from "./pages/root/monotone-root-page";
import { MonotoneAreasPage } from "./pages/areas/monotone-areas-page";
import { customRoutes } from "../../custom-design/custom-routes";
import { CustomContextProvider } from "../../custom-design/custom-context-provider";

interface MonotoneAppContainerProps {
  designSettings: ApplicationDesignSettings | null;
  error: string | null;
}

interface MonotoneTemplateSettings {
  accentColor?: string;
  backgroundColor?: string;
  textColor?: string;
  fontFamily?: string;
  fontSize?: number;
}

const Router = () => {
  const location = useLocation();
  const applicationKey = import.meta.env.VITE_APPLICATION_KEY;

  // Create Modules
  const navigationCoreModule = useNavigationCoreModule();
  const navigationModule = useNavigationModule(
    applicationConfig,
    navigationCoreModule
  );
  useQueryModule();

  const activityLogModule = ServiceLocator.ActivityLogModule;
  useEffect(() => {
    activityLogModule.sendPageViewEvent(location.pathname, location.pathname);
  }, [location.pathname]);

  return (
    <NavigationCoreModuleProvider module={navigationCoreModule}>
      <NavigationModuleProvider module={navigationModule}>
        <CustomContextProvider applicationKey={applicationKey}>
          <AnimatePresence mode="wait" initial={false}>
            <Routes location={location} key={location.pathname}>
              <Route path={PagePaths.note} element={<MonotoneNotePage />} />
              <Route path={PagePaths.top} element={<MonotoneTopPage />} />
              <Route path={PagePaths.areas} element={<MonotoneAreasPage />} />
              <Route path={PagePaths.guides} element={<MonotoneGuidesPage />} />
              <Route path={PagePaths.root} element={<MonotoneRootPage />} />
              {customRoutes({ applicationKey }).map((route) => (
                <Route
                  path={route.path}
                  element={route.element}
                  key={route.path}
                />
              ))}
              <Route path="/*" element={<NotFoundPage />} />
            </Routes>
          </AnimatePresence>
        </CustomContextProvider>
      </NavigationModuleProvider>
    </NavigationCoreModuleProvider>
  );
};

const NotFoundPage = () => <div>Not Found</div>;

const MonotoneAppContainer: React.FC<MonotoneAppContainerProps> = ({
  designSettings,
  error,
}) => {
  const theme = ServiceLocator.ThemeStore.theme;
  const fontClass = useLanguageBasedFont();

  // テンプレート設定値の取得・パース
  const templateSettings = React.useMemo<MonotoneTemplateSettings>(() => {
    try {
      return designSettings?.templateConfiguration
        ? JSON.parse(designSettings.templateConfiguration)
        : {};
    } catch (error) {
      console.warn("Failed to parse monotone template configuration:", error);
      return {};
    }
  }, [designSettings?.templateConfiguration]);

  // Monotone テーマの適用（設定値を反映）
  const monotoneTheme = {
    ...theme,
    color: {
      ...theme.color,
      primaryBackground: templateSettings.backgroundColor || "#ffffff",
      secondaryBackground: "#f5f5f5",
      primaryText: templateSettings.textColor || "#000000",
      secondaryText: "#666666",
      accent: templateSettings.accentColor || "#333333",
    },
    font: {
      family: templateSettings.fontFamily || "monospace",
      size: templateSettings.fontSize || 14,
    },
  };

  return (
    <>
      {error && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: "#ffebee",
            color: "#c62828",
            padding: "8px",
            fontSize: "12px",
            zIndex: 9999,
            textAlign: "center",
          }}
        >
          {error}
        </div>
      )}

      <motion.div
        className={`overflow-hidden h-[calc(var(--vh,1vh)*100)] ${fontClass}`}
        style={{
          background: monotoneTheme.color.primaryBackground,
          color: monotoneTheme.color.primaryText,
          fontFamily: monotoneTheme.font.family,
          fontSize: `${monotoneTheme.font.size}px`,
        }}
        animate={{
          scale: 1,
          y: 0,
          borderRadius: 0,
        }}
        transition={{
          type: "tween",
          duration: 0.3,
        }}
      >
        <BrowserRouter>
          <Router />
        </BrowserRouter>
        <motion.div
          animate={{
            opacity: 0,
          }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 3000,
            pointerEvents: "none",
            background: monotoneTheme.color.secondaryBackground,
          }}
        />
      </motion.div>
    </>
  );
};

export default MonotoneAppContainer;
